import React from 'react'
import {ORDER_CONDITION_TYPES, ORDER_CONDITION_VARIABLES} from 'server/functions/orders/orderConditions.js'
import type {OrderConditionVariable} from 'server/functions/orders/orderConditions.js'
import type {Condition, ConditionValue} from 'server/utils/conditions.js'
import {Select, TimePicker, MetricInput} from '@components/UI/forms/index.js'
import t from 'src/helpers/Languages.js'
import dbCodes from 'server/dbCodes.js'
import {Customer} from 'server/functions/customers/customers.types'
import {Reseller} from 'types/types.js'
import ConditionGroupsEditor, {ConditionGroupsEditorProps, ConditionVariableOption} from './ConditionGroupsEditor.js'

const VARIABLE_TITLES: Record<OrderConditionVariable, () => string> = {
    customer: () => t('customer'),
    sender: () => t('consignor'),
    creationTime: () => t('creationTimeOrderDate'),
    creationTimeOfDay: () => t('creationTimeOfDay'),
    startTime: () => t('timeslotStartTime'),
    endTime: () => t('timeslotEndTime'),
    colliType: () => t('colloType'),
    colliAmount: () => t('colloAmount'),
    colliWeight: () => t('colloWeight'),
    colliVolume: () => t('colloVolume'),
    options: () => t('options'),
    deliveryZone: () => t('deliveryZone'),
    postalCode: () => t('postalCode'),
    notes: () => t('notes'),
    reference: () => t('reference'),
    instruction: () => t('instruction'),
    street: () => t('street'),
    city: () => t('city')
}

interface OrderConditionGroupsEditorProps extends ConditionGroupsEditorProps<OrderConditionVariable> {
    /** The variables that can be chosen, by default all variables of an order */
    variables?: OrderConditionVariable[]
    customers: {
        [customerId: string]: Customer
    }
    reseller: Reseller
}

/**
 * Edits conditions on orders: route templates (Route automatisch vullen), partner connections, automatic order fees
 * and order products
 */
function OrderConditionGroupsEditor({variables = ORDER_CONDITION_VARIABLES, customers, reseller, ...props}: OrderConditionGroupsEditorProps) {
    const variableOptions: ConditionVariableOption<OrderConditionVariable>[] = variables.map((variable) => ({value: variable, title: VARIABLE_TITLES[variable]()}))

    const optionOptions = [
        ...Object.keys(dbCodes.pickupAndDeliveryOptions).map((option) => ({value: option, title: dbCodes.pickupAndDeliveryOptions[option]})),
        ...(reseller.settings.orders.customOptions || []).map((option) => ({value: option.id, title: option.name, icon: 'mdi mdi-circle', iconColor: option.color, showIconInValue: true}))
    ]

    const renderValue = (condition: Condition<OrderConditionVariable>, onChange: (value: ConditionValue) => void) => {
        const values = Array.isArray(condition.value) ? condition.value : []

        switch (condition.variable) {
            case 'customer':
                return (
                    <Select
                        values={values}
                        options={Object.entries(customers).filter(([, customer]) => !customer.isArchived).map(([customerId, customer]) => ({value: customerId, title: customer.name}))}
                        onChange={(event) => onChange(event.target.values)}
                    />
                )
            case 'creationTime':
            case 'creationTimeOfDay':
                return (
                    <TimePicker
                        info={condition.variable === 'creationTime' ? t('creationTimeOrderDateInfo') : t('creationTimeOfDayInfo')}
                        value={condition.value as string}
                        onChange={(event) => onChange(event.target.value)}
                    />
                )
            case 'colliType':
                return (
                    <Select
                        values={values}
                        options={(reseller.settings.orders.colliTypes || []).map((type) => ({value: type.id, title: type.name}))}
                        onChange={(event) => onChange(event.target.values.filter((value) => value))}
                    />
                )
            case 'options':
                return (
                    <Select
                        values={values}
                        options={optionOptions}
                        onChange={(event) => onChange(event.target.values)}
                    />
                )
            case 'deliveryZone':
                return (
                    <Select
                        values={values}
                        options={reseller.settings.deliveryZones.map((deliveryZone) => ({value: deliveryZone.id, title: deliveryZone.name}))}
                        onChange={(event) => onChange(event.target.values)}
                    />
                )
            case 'colliWeight':
                return (
                    <MetricInput
                        max={999999999}
                        divisionFactor={1000}
                        append='kg'
                        value={condition.value as number}
                        onChange={(event) => onChange(event.target.value)}
                    />
                )
            case 'colliVolume':
                return (
                    <MetricInput
                        append='L'
                        min={0}
                        value={condition.value as number}
                        onChange={(event) => onChange(event.target.value)}
                    />
                )
            default:
                return undefined
        }
    }

    return (
        <ConditionGroupsEditor
            {...props}
            variables={variableOptions}
            types={ORDER_CONDITION_TYPES}
            renderValue={renderValue}
        />
    )
}

export default OrderConditionGroupsEditor
